import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import 'moment/locale/de';
import { dayOfYear, daysInYear, getMonthsOfYear } from './vacationUtils';
import { hasPermissions } from '../../../auth/hasPermissions';

export const COMPANY_HOLIDAY_COLOR = '#00897b';

export const CompanyHolidayRow = ({
  companyHolidays,
  year,
  onClickHoliday,
  onCreateNew,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const canManage = hasPermissions('employee-request-management', 'write');
  const locale = i18n.language || 'de';
  const totalDays = daysInYear(year);
  const months = getMonthsOfYear(year, locale);
  const toPercent = (days) => (days / totalDays) * 100;
  const formatDate = (date) =>
    moment(date).locale(locale).format('dd, DD.MM.YYYY');

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${colors.selected}`,
        bgcolor: `${COMPANY_HOLIDAY_COLOR}08`,
      }}
      className="@container flex items-stretch"
    >
      {/* Left info column */}
      <Box className="flex items-center gap-2 py-2 px-2 @md:px-3 shrink-0 w-[56px] @md:w-[280px]">
        <BusinessIcon sx={{ color: COMPANY_HOLIDAY_COLOR, fontSize: 28 }} />
        <Box className="min-w-0 hidden @md:flex items-center gap-1">
          <Typography fontWeight={600} fontSize={14} noWrap>
            {t('Company Holiday')}
          </Typography>
          {canManage && (
            <IconButton
              size="small"
              onClick={onCreateNew}
              sx={{ color: COMPANY_HOLIDAY_COLOR }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Right timeline */}
      <Box
        className="flex-1 py-1"
        onDoubleClick={canManage ? onCreateNew : undefined}
        sx={{ cursor: canManage ? 'crosshair' : 'default' }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: 50,
          }}
        >
          {/* Month separators */}
          {months.map((month, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: `${toPercent(month.startDay)}%`,
                top: 0,
                bottom: 0,
                borderLeft: `1px solid ${colors.selected}`,
              }}
            />
          ))}

          {/* Company holiday blocks */}
          {companyHolidays.map((holiday, i) => {
            const startIdx = dayOfYear(holiday.startDate, year);
            const endIdx = dayOfYear(holiday.endDate, year);
            const span = endIdx - startIdx + 1;

            const attributes = [
              holiday.isPaid ? t('Paid') : t('Unpaid'),
              holiday.isVacation ? t('Counts as vacation') : null,
              holiday.startsWithHalfDay ? t('Starts with half day') : null,
              holiday.endsWithHalfDay ? t('Ends with half day') : null,
            ].filter(Boolean);

            return (
              <Tooltip
                key={`ch-${i}`}
                title={`${t('Company Holiday')}: ${formatDate(holiday.startDate)} – ${formatDate(holiday.endDate)} (${span} ${span > 1 ? t('Days') : t('Day')}) — ${attributes.join(', ')}`}
              >
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickHoliday(holiday);
                  }}
                  sx={{
                    position: 'absolute',
                    left: `${toPercent(startIdx)}%`,
                    width: `${toPercent(span)}%`,
                    top: '20%',
                    height: '60%',
                    bgcolor: COMPANY_HOLIDAY_COLOR,
                    opacity: 0.85,
                    borderRadius: '3px',
                    zIndex: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
